import React, {useEffect, useState} from "react"
import {MessageBannerItem} from "models/messageBanner"
import {SlideWrapper, SlideItem, MainWrapper, SlideContent} from "./component"

export type SliderBannerProps = {
    items: MessageBannerItem[]
    autoplayTime: number
    pauseOnHover: boolean
    infinite: boolean
}

export type SliderContentProps = {
    items: MessageBannerItem[]
    activeIndex: number
}

const SliderContent = ({activeIndex, items}: SliderContentProps) => {
    const isSingleItem = items.length === 1
    const isNext = (index: number) => (activeIndex === items.length - 1 ? index === 0 : index === activeIndex + 1)
    return (
        <SlideWrapper>
            {items.map((slide, index) => (
                <SlideItem
                    key={index}
                    hidden={isSingleItem ? false : index !== activeIndex}
                    next={isSingleItem ? false : isNext(index)}
                >
                    <SlideContent
                        data-testid="header-slider-content"
                        dangerouslySetInnerHTML={{__html: slide.teaserTextPlain || ""}}
                    />
                </SlideItem>
            ))}
        </SlideWrapper>
    )
}

export const MessageBannerCarousel = ({items, autoplayTime, infinite, pauseOnHover}: SliderBannerProps) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null)
    const [isContainerReady, setIsContainerReady] = useState(false)
    useEffect(() => {
        if (containerRef.current) {
            setIsContainerReady(true)
        }
    }, [])
    const clonedItems = items
    if (items.length === 2) {
        // Duplicate the items if there are only two items so the animation works correctly
        clonedItems.push(...items)
    }

    const length = clonedItems.length - 1
    const [activeIndex, setActiveIndex] = useState(0)
    const [currentContentId, setCurrentContentId] = useState("")
    const [isPaused, setIsPaused] = useState(false)

    useEffect(() => {
        if (isPaused) return
        const interval = setInterval(() => {
            if (infinite) {
                setActiveIndex(activeIndex === length ? 0 : activeIndex + 1)
                setCurrentContentId(clonedItems[activeIndex === length ? 0 : activeIndex + 1]?.contentId || "")
            } else if (activeIndex < length) {
                setActiveIndex(activeIndex + 1)
                setCurrentContentId(clonedItems[activeIndex + 1]?.contentId || "")
            }
        }, autoplayTime)
        return () => clearInterval(interval)
    }, [activeIndex, isPaused, infinite])

    const handleMouseEnter = () => setIsPaused(pauseOnHover)
    const handleMouseLeave = () => setIsPaused(false)

    return (
        <MainWrapper
            data-testid="header-slider-container"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            ref={containerRef}
            data-contentid={currentContentId}
        >
            {isContainerReady && <SliderContent activeIndex={activeIndex} items={items} />}
        </MainWrapper>
    )
}

export default MessageBannerCarousel
